import type {
  AutoPrintCatalog,
  AutoPrintRule,
  CreateAutoPrintRuleRequest,
  UpdateAutoPrintRuleRequest,
} from '@techgitter/contracts';

const API_BASE_URL = '/api';

/**
 * Admin AutoPrint-rules client. Talks to the admin endpoints behind the same
 * SWA-header admin gate as `/api/printers`:
 *
 *   GET    /api/autoprint/catalog          — code catalog (triggers + documents)
 *   GET    /api/autoprint_rules            — list bindings (incl. inactive)
 *   POST   /api/autoprint_rules            — create a binding
 *   PATCH  /api/autoprint_rules/{id}       — update a binding
 *   DELETE /api/autoprint_rules/{id}       — delete a binding
 *
 * The catalog drives the create/edit pickers so a binding can only reference a
 * trigger / document that exists; the write endpoints validate the same and
 * return a 400 `{ error }` for an unknown trigger / document, a printer that
 * doesn't exist, or `fixed` mode without a valid copies count.
 */

/** GET /api/autoprint/catalog — code-defined triggers + documents. */
export async function fetchAutoPrintCatalog(
  signal?: AbortSignal,
): Promise<AutoPrintCatalog> {
  const response = await fetch(`${API_BASE_URL}/autoprint/catalog`, {
    method: 'GET',
    headers: { Accept: 'application/json' },
    signal,
  });
  if (!response.ok) {
    throw new Error(
      `Failed to fetch AutoPrint catalog: ${response.status} ${response.statusText}`,
    );
  }
  return response.json();
}

/** GET /api/autoprint_rules — list bindings (admin default includes inactive). */
export async function fetchAutoPrintRules(
  signal?: AbortSignal,
  options?: { trigger?: string; includeInactive?: boolean },
): Promise<AutoPrintRule[]> {
  const params = new URLSearchParams();
  if (options?.trigger) params.set('trigger', options.trigger);
  // Admin default is to include inactive; only send the flag when opting out.
  if (options?.includeInactive === false) params.set('includeInactive', 'false');
  const query = params.toString() ? `?${params.toString()}` : '';
  const response = await fetch(`${API_BASE_URL}/autoprint_rules${query}`, {
    method: 'GET',
    headers: { Accept: 'application/json' },
    signal,
  });
  if (!response.ok) {
    throw new Error(
      `Failed to fetch AutoPrint rules: ${response.status} ${response.statusText}`,
    );
  }
  const data = await response.json();
  return data.items ?? [];
}

export async function createAutoPrintRule(
  payload: CreateAutoPrintRuleRequest,
  signal?: AbortSignal,
): Promise<AutoPrintRule> {
  const response = await fetch(`${API_BASE_URL}/autoprint_rules`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    signal,
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({
      error: `Failed to create AutoPrint rule: ${response.status}`,
    }));
    throw new Error(err.error || `HTTP ${response.status}`);
  }
  return response.json();
}

export async function updateAutoPrintRule(
  ruleId: string,
  payload: UpdateAutoPrintRuleRequest,
  signal?: AbortSignal,
): Promise<AutoPrintRule> {
  const response = await fetch(`${API_BASE_URL}/autoprint_rules/${ruleId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    signal,
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({
      error: `Failed to update AutoPrint rule: ${response.status}`,
    }));
    throw new Error(err.error || `HTTP ${response.status}`);
  }
  return response.json();
}

export async function deleteAutoPrintRule(
  ruleId: string,
  signal?: AbortSignal,
): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/autoprint_rules/${ruleId}`, {
    method: 'DELETE',
    signal,
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({
      error: `Failed to delete AutoPrint rule: ${response.status}`,
    }));
    throw new Error(err.error || `HTTP ${response.status}`);
  }
}
